import type {
  BoardChunkDefinition,
  CellStackDefinition,
  ColorId,
  LevelDefinition,
  Point,
  TargetSlotDefinition,
} from "@pindou/game-core";

interface RegionSpec {
  readonly id: string;
  readonly origin: Point;
  readonly topColor: ColorId;
  readonly supportColor: ColorId;
}

const regions: readonly RegionSpec[] = [
  { id: "region-a", origin: { x: 1, y: 1 }, topColor: "coral", supportColor: "jade" },
  { id: "region-b", origin: { x: 5, y: 1 }, topColor: "mango", supportColor: "violet" },
  { id: "region-c", origin: { x: 9, y: 1 }, topColor: "rose", supportColor: "jade" },
  { id: "region-d", origin: { x: 1, y: 7 }, topColor: "sky", supportColor: "violet" },
  { id: "region-e", origin: { x: 5, y: 7 }, topColor: "cream", supportColor: "jade" },
  { id: "region-f", origin: { x: 9, y: 7 }, topColor: "graphite", supportColor: "violet" },
];

const targetColors: readonly ColorId[] = [
  "coral",
  "mango",
  "rose",
  "sky",
  "cream",
  "graphite",
  "jade",
  "violet",
];

function makeRegion(spec: RegionSpec): BoardChunkDefinition {
  const cells: CellStackDefinition[] = [];
  for (let y = 0; y < 6; y += 1) {
    for (let x = 0; x < 4; x += 1) {
      const index = y * 4 + x;
      const bottomColor = y < 3 ? spec.topColor : spec.supportColor;
      const bottomId = `${spec.id}-bottom-${index}`;
      const topId = `${spec.id}-top-${index}`;
      cells.push({
        x,
        y,
        layers: [
          { id: bottomId, color: bottomColor },
          { id: topId, color: spec.topColor },
        ],
      });
    }
  }
  return { id: spec.id, origin: spec.origin, cells };
}

function makeTargetSlots(): readonly TargetSlotDefinition[] {
  const slots: TargetSlotDefinition[] = [];
  for (let y = 1; y <= 12; y += 1) {
    for (let x = 1; x <= 12; x += 1) {
      const targetRegion = Math.floor((x - 1) / 3) + (y > 6 ? 4 : 0);
      const color = targetColors[targetRegion];
      if (!color) {
        throw new Error("Missing target color");
      }
      for (let depth = 0; depth < 2; depth += 1) {
        slots.push({ id: `target-${x}-${y}-${depth}`, color, x, y });
      }
    }
  }
  return slots;
}

const targetSlots = makeTargetSlots();

export const level14: LevelDefinition = {
  id: "level-14",
  title: "第14关",
  durationMs: 100_000,
  board: {
    width: 14,
    height: 14,
    chunks: regions.map(makeRegion),
  },
  targetSlots,
  rules: {
    trayCapacity: 36,
    maxTrayCapacity: 60,
    matchSize: 36,
    failOnTimeout: true,
  },
};

/** Deterministic solution used by smoke tests and non-visual shell tests. */
export const level14Solution: readonly Point[] = [
  { x: 1, y: 1 }, { x: 1, y: 1 },
  { x: 5, y: 1 }, { x: 5, y: 1 },
  { x: 9, y: 1 }, { x: 9, y: 1 },
  { x: 1, y: 7 }, { x: 1, y: 7 },
  { x: 5, y: 7 }, { x: 5, y: 7 },
  { x: 9, y: 7 }, { x: 9, y: 7 },
  { x: 1, y: 4 }, { x: 9, y: 4 }, { x: 5, y: 10 },
  { x: 5, y: 4 }, { x: 1, y: 10 }, { x: 9, y: 10 },
];
